import React from "react";
import { styled } from "@mui/material/styles";
import { Box, Paper, Button, Typography } from "@mui/material";
import { BigGreenCheck } from "./icons/BigGreenCheck";
import { SmallGoldIcon } from "./icons/SmallGoldIcon";

// --- Layout components from ladder-styles ---

export const FullPanel = styled(Box)({
  label: "FullPanel",
  position: "absolute",
  padding: "0px 4px 4px 4px",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  bgcolor: "background.paper",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "flex-start",
  maxWidth: "unset",
  overflowY: "auto",
});

export const FullScreen = styled(Box)({
  label: "FullScreen",
  position: "fixed",
  padding: "0px 10px 10px 10px",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  bgcolor: "background.paper",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "flex-start",
  maxWidth: "unset",
  overflowY: "auto",
});

export const LadderScreenTopBar = styled(Box)({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
  padding: "10px",
});

export const LadderStartScreenLayout = styled(Paper)({
  display: "flex",
  flexDirection: "column",
  maxWidth: "960px",
  alignItems: "center",
  justifyContent: "space-around",
  label: "LadderStartScreenLayout",
  gap: 20,
  height: "auto",
  bgcolor: "background.paper",
  padding: 10,
});

export const TopRightLabel = styled(Box)({});

export const TopLeftButton = styled(Box)({});

export const ActionButton = styled(Button)({
  minWidth: "80vw",
  padding: "15px 10px",
  "@media (min-width: 600px)": {
    minWidth: "400px",
  },
});

export const IconPosition = styled("div")({
  position: "relative",
  display: "inline-block",
  marginRight: 10,
  top: 5,
  left: 0,
});

export const LadderImage = styled("img")({
  maxWidth: "90vw",
  minWidth: "100px",
  flexShrink: 1,
  "@media (min-width: 600px)": {
    maxWidth: "400px",
  },
});

const QuizLabel = styled("span")(({ theme }) => ({
  color: theme.palette.primary.main,
}));

// --- Completion components from ladder-completion-styles ---

export const LadderComplete = ({
  xp,
  isQuiz,
  isFirstCompleted,
  scorePercent,
}: {
  xp?: number;
  isQuiz: boolean;
  isFirstCompleted: boolean;
  scorePercent?: number;
}) => (
  <>
    <Typography variant="h2">Ladder Complete</Typography>
    <BigGreenCheck />
    {isQuiz && (
      <Typography variant="h4" textAlign="center">
        {scorePercent != null
          ? `You scored ${scorePercent}% and passed the `
          : "You passed the "}
        <QuizLabel>Quiz</QuizLabel>!
      </Typography>
    )}
    {isFirstCompleted && (
      <Typography variant="h4" color="success.dark">
        You earned {xp} XP
      </Typography>
    )}
  </>
);

export const LadderPerfect = ({
  xp,
  bonusXp,
  isFirstCompleted,
}: {
  xp?: number;
  bonusXp?: number;
  isFirstCompleted: boolean;
}) => (
  <>
    <Typography variant="h2">Perfect!</Typography>
    <SmallGoldIcon width={128} height={128} />
    <Typography variant="h4" textAlign="center">
      You passed the <QuizLabel>Quiz</QuizLabel> with 100%
    </Typography>
    {isFirstCompleted && (
      <Typography variant="h4" color="success.dark">
        You earned {xp} XP
        {bonusXp != null && bonusXp > 0 && ` + ${bonusXp} XP bonus!`}
      </Typography>
    )}
  </>
);

export const LadderFailed = ({ scorePercent }: { scorePercent?: number }) => (
  <>
    <Typography variant="h2">Still climbing...</Typography>
    <LadderImage
      src={new URL("../assets/ladder_climb.svg", import.meta.url).href}
      alt="Ladder"
    />
    {scorePercent != null && (
      <Typography variant="h4" textAlign="center" color="warning.dark">
        You scored {scorePercent}%.
      </Typography>
    )}
    <Typography textAlign="center" variant="h4">
      You need 80% to pass the <QuizLabel>Quiz</QuizLabel> and complete this
      Ladder.
    </Typography>
  </>
);

export const TryAgainExitButtons = ({
  handleBackNavigation,
  onReplay,
}: {
  handleBackNavigation: () => void;
  onReplay: () => void;
}) => (
  <>
    <ActionButton variant="contained" color="primary" onClick={onReplay}>
      Try Again
    </ActionButton>
    <ActionButton
      variant="outlined"
      color="primary"
      onClick={handleBackNavigation}
    >
      Exit Ladder
    </ActionButton>
  </>
);

export const ContinueButton = ({
  handleBackNavigation,
}: {
  handleBackNavigation: () => void;
}) => (
  <ActionButton
    variant="contained"
    color="primary"
    onClick={handleBackNavigation}
  >
    Continue
  </ActionButton>
);
